
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/user';
import { UserRoleType } from '@/types/roles';

export function useUserFetch() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        // Get all users from user_roles table
        const { data: userRoles, error: userRolesError } = await supabase
          .from('user_roles')
          .select('*');
        
        if (userRolesError) throw new Error(userRolesError.message);

        // Try to get email information if possible
        let emailMap = new Map<string, string>();
        
        try {
          // Try admin list users (requires service role)
          const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
          
          if (!authError && authUsers && authUsers.users) {
            authUsers.users.forEach((user: any) => {
              if (user && user.id && user.email) {
                emailMap.set(user.id, user.email);
              }
            });
          }
        } catch (err) {
          console.log("Could not access auth.admin.listUsers, using fallback method");
        }

        // Combine the data
        if (userRoles) {
          const combinedUsers: User[] = userRoles.map((userRole: any) => {
            const role = userRole.role as UserRoleType || 'viewer';
            return {
              id: userRole.user_id,
              email: emailMap.get(userRole.user_id) || `user-${userRole.user_id.substring(0, 8)}@example.com`,
              role: role,
              is_active: userRole.is_active !== undefined ? userRole.is_active : true,
              created_at: new Date(userRole.created_at || '').toISOString(),
              organization_id: userRole.team_id
            };
          });

          setUsers(combinedUsers);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  return { users, isLoading, error };
}
